<template>
  <AppShell :title="user ? `${user.givenName} ${user.surname}` : 'Kund'">
    <p v-if="isLoading" class="text-subtle">Hämtar kund...</p>

    <template v-else-if="user">
      <AppTabs class="user-tabs">
        <template #left>
          <AppRouterLink :to="basePath" variant="nav" class="nav-link" exact-active-class="nav-link-active">
            Kund
          </AppRouterLink>
          <AppRouterLink :to="`${basePath}/dokument`" variant="nav" class="nav-link" active-class="nav-link-active">
            Dokument
          </AppRouterLink>
        </template>
      </AppTabs>

      <router-view :user="user" :on-kyc-submitted="loadUser" />
    </template>
  </AppShell>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { AppTabs, notify } from '@staff/ui'
import AppShell from '@/components/AppShell.vue'
import AppRouterLink from '@/components/AppRouterLink.vue'
import { fetchUserById, type UserDetails } from '@/services/user'

const route = useRoute()

const isLoading = ref(true)
const user = ref<UserDetails>(null!)

const userId = computed(() => route.params.id as string)
const basePath = computed(() => `/kunder/${userId.value}`)

const loadUser = async (): Promise<boolean> => {
  if (!userId.value) return false

  isLoading.value = true

  try {
    user.value = await fetchUserById(userId.value)
    return true
  } catch (err) {
    console.error('Failed to load user', err)
    const message =
      err instanceof TypeError
        ? 'Ett fel uppstod. Försök igen.'
        : err instanceof Error
          ? err.message
          : 'Ett fel uppstod när kunden hämtades.'
    notify({ type: 'error', title: message, dismissable: true })
    return false
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  void loadUser()
})

watch(userId, () => {
  void loadUser()
})
</script>

<style scoped lang="scss">
.user-tabs {
  --app-tabs-bg: var(--color-canvas-inset);

  margin-bottom: var(--space-24);
  padding-inline: var(--space-16);
}

.nav-link:hover {
  background-color: var(--color-button-default-hover-bg);
}

.nav-link-active {
  background-color: var(--color-brand-subtle);
  color: var(--color-brand-default);
  font-weight: var(--font-weight-semibold);
}
</style>
